import type {
  TeamMember,
  VisitorInformation
} from "../../../features/Booking-Form/typings/team-member";
import {
  BookingType,
  type NotSerializedBookingInputs,
  type VisitorRegistrationEntry
} from "../../../features/Booking-Form/typings/booking-inputs";
import {
  filterSubstantiveVisitorRegistrations,
  visitorRequestsPlaceOrRoom
} from "../../../features/Booking-Form/functions/visitor-registration.functions";

/** Team confirm step: show visitor summary when at least one invitation row exists. */
export function teamBookingHasVisitorInvitations(inputs: NotSerializedBookingInputs): boolean {
  return inputs.mode === BookingType.TEAM && (inputs.visitorRegistrations?.length ?? 0) > 0;
}

/** Conference confirm step: show visitor summary when at least one invitation row exists. */
export function conferenceBookingHasVisitorInvitations(
  inputs: NotSerializedBookingInputs
): boolean {
  return (
    inputs.mode === BookingType.CONFERENCEZONE && (inputs.visitorRegistrations?.length ?? 0) > 0
  );
}

/**
 * True when the booking confirm column shows a floor plan next to the visitor confirm card
 * (visitor book-place, or team/conference booking with visitor invitations), so the visitor list
 * stays short.
 */
export function visitorConfirmCompanionFloorPlanVisible(
  inputs: NotSerializedBookingInputs
): boolean {
  if (inputs.isMobileWorking || inputs.automatedSeating) {
    return false;
  }
  if (inputs.mode === BookingType.VISITOR) {
    return visitorRequestsPlaceOrRoom(inputs);
  }
  if (inputs.mode === BookingType.TEAM) {
    return teamBookingHasVisitorInvitations(inputs);
  }
  if (inputs.mode === BookingType.CONFERENCEZONE) {
    return conferenceBookingHasVisitorInvitations(inputs);
  }
  return false;
}

/**
 * Maps a completed visitor registration row to the BE payload entry for conference room bookings.
 * Conference visitors are always visit-only (no desk inventory), so the payload only includes the
 * visit-only fields needed to create the matching visitor registrations on the backend.
 */
export type ConferenceVisitorRegistrationDto = {
  firstName: string;
  surname: string;
  email: string;
  company?: string;
  contacts?: string;
  visitReason?: string;
  locationInventoryId: number;
  locationName?: string;
  timezone?: string;
  isMobileWorking?: boolean;
};

export type ConferenceRoomLocation = {
  locationInventoryId: number;
  locationName?: string | null;
  timezone?: string | null;
  isMobileWorking?: boolean;
};

export function conferenceRoomLocationFromInputs(
  inputs: Pick<
    NotSerializedBookingInputs,
    "selectedLocation" | "locationName" | "timezone" | "isMobileWorking"
  >
): ConferenceRoomLocation | null {
  if (inputs.selectedLocation == null) {
    return null;
  }
  return {
    locationInventoryId: inputs.selectedLocation,
    locationName: inputs.locationName,
    timezone: inputs.timezone,
    isMobileWorking: inputs.isMobileWorking
  };
}

export function visitorRegistrationEntryToConferenceVisitorDto(
  entry: VisitorRegistrationEntry,
  roomLocation?: ConferenceRoomLocation | null
): ConferenceVisitorRegistrationDto {
  const locationInventoryId = entry.visitOnlySelectedLocation ?? roomLocation?.locationInventoryId;
  const trimmedEntryLocationName = entry.visitOnlyLocationName?.trim();
  const trimmedRoomLocationName = roomLocation?.locationName?.trim();
  let locationName: string | undefined;
  if (trimmedEntryLocationName) {
    locationName = trimmedEntryLocationName;
  } else if (trimmedRoomLocationName) {
    locationName = trimmedRoomLocationName;
  }
  const trimmedEntryTimezone = entry.visitOnlyTimezone?.trim();
  const trimmedRoomTimezone = roomLocation?.timezone?.trim();
  let timezone: string | undefined;
  if (trimmedEntryTimezone) {
    timezone = trimmedEntryTimezone;
  } else if (trimmedRoomTimezone) {
    timezone = trimmedRoomTimezone;
  }

  return {
    firstName: entry.firstName.trim(),
    surname: entry.surname.trim(),
    email: entry.email.trim(),
    company: entry.company?.trim() ? entry.company.trim() : undefined,
    contacts: entry.contacts?.trim() ? entry.contacts.trim() : undefined,
    visitReason: entry.visitReason?.trim() ? entry.visitReason.trim() : undefined,
    locationInventoryId: Number(locationInventoryId),
    locationName,
    timezone,
    isMobileWorking: entry.visitOnlyIsMobileWorking ?? roomLocation?.isMobileWorking ?? false
  };
}

export function buildConferenceVisitorRegistrationDtos(
  registrations: VisitorRegistrationEntry[] | undefined,
  roomLocation?: ConferenceRoomLocation | null
): ConferenceVisitorRegistrationDto[] {
  if (!registrations?.length) {
    return [];
  }
  return filterSubstantiveVisitorRegistrations(registrations)
    .filter(
      entry =>
        (entry.visitOnlySelectedLocation ?? roomLocation?.locationInventoryId) != null &&
        Boolean(entry.firstName?.trim() && entry.surname?.trim() && entry.email?.trim())
    )
    .map(entry => visitorRegistrationEntryToConferenceVisitorDto(entry, roomLocation));
}

/**
 * Maps a completed visitor registration row to a team-booking guest (legacy `isExternal` shape for availability/confirm).
 */
export function visitorRegistrationEntryToTeamBookingGuest(
  entry: VisitorRegistrationEntry,
  bookerSub: string
): TeamMember {
  const visitorInformation: VisitorInformation = {
    firstName: entry.firstName.trim(),
    surname: entry.surname.trim(),
    email: entry.email.trim(),
    company: entry.company?.trim() ? entry.company.trim() : undefined,
    contacts: entry.contacts?.trim() ? entry.contacts.trim() : undefined,
    visitReason: entry.visitReason?.trim() ? entry.visitReason.trim() : undefined
  };

  return {
    userBookedById: bookerSub,
    userId: bookerSub,
    firstName: entry.firstName.trim(),
    surname: entry.surname.trim(),
    email: entry.email.trim(),
    company: entry.company?.trim() ? entry.company.trim() : undefined,
    isExternal: true,
    isVisitor: true,
    bookPlace: entry.bookPlace === true,
    visitorInformation,
    bookingInventoryId: undefined
  } as TeamMember;
}
